package com.evoapps.evoshift.ui.user

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.ContentPaste
import androidx.compose.material.icons.filled.Description
import androidx.compose.material.icons.filled.ErrorOutline
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.TableChart
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.domain.spreadsheet.XlsxReader
import com.evoapps.evoshift.domain.staffimport.StaffImportField
import com.evoapps.evoshift.domain.staffimport.StaffRowNote
import com.evoapps.evoshift.domain.staffimport.StaffRowProblem
import com.evoapps.evoshift.spreadsheet.SpreadsheetDropZone
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.FormMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.billing.PlanLimitDialog
import com.evoapps.evoshift.ui.finance.formatMoney
import com.evoapps.evoshift.ui.util.HelpPage
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.viewmodels.StaffImportColumn
import com.evoapps.evoshift.viewmodels.StaffImportRowState
import com.evoapps.evoshift.viewmodels.StaffImportStep
import com.evoapps.evoshift.viewmodels.StaffImportUiState
import com.evoapps.evoshift.viewmodels.StaffImportViewModel
import com.evoapps.evoshift.widgets.DataRow
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.GoldButton
import com.evoapps.evoshift.widgets.RegularCard
import com.evoapps.evoshift.widgets.helpAction
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.*
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** Test tags of the staff import screen. */
object StaffImportTags {
    const val PASTE = "staff_import_paste"
    const val READ_PASTED = "staff_import_read_pasted"
    const val TEMPLATE = "staff_import_template"
    const val HAS_HEADER = "staff_import_has_header"
    const val TO_PREVIEW = "staff_import_to_preview"
    const val SKIP_ALL = "staff_import_skip_all"
    const val APPLY = "staff_import_apply"
    const val DONE = "staff_import_done"
    fun column(index: Int) = "staff_import_column_$index"
    fun row(number: Int) = "staff_import_row_$number"
    fun fix(number: Int) = "staff_import_fix_$number"
    fun skip(number: Int) = "staff_import_skip_$number"
    fun editField(field: StaffImportField) = "staff_import_edit_${field.name.lowercase()}"
}

/**
 * SH-214: Team → "Import from a spreadsheet" ([StaffImportViewModel]). Step by step: the source
 * (a file dropped or picked, or cells pasted; the CSV template), the column mapping, the people
 * checked (fix or skip a row with problems), then "Import N people" and the summary.
 */
@Composable
fun StaffImportScreen(
    viewModel: StaffImportViewModel,
    onNavigateBack: () -> Unit,
    onSeePlans: () -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val messageText = state.message?.asString()
    LaunchedEffect(messageText) {
        messageText?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }
    state.planLimit?.let { limit ->
        PlanLimitDialog(
            limit = limit,
            onSeePlans = { viewModel.planLimitShown(); onSeePlans() }.takeIf { state.canChoosePlan },
            onDismiss = viewModel::planLimitShown
        )
    }
    state.editing?.let { row -> EditRowDialog(state, row, onSave = viewModel::saveEdit, onDismiss = viewModel::cancelEdit) }

    Scaffold(
        containerColor = Cream,
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            EvoTopBar(
                title = stringResource(Res.string.staff_import_title),
                onBack = onNavigateBack,
                showWorkplace = true,
                actions = listOf(helpAction(HelpPage.TEAM_IMPORT))
            )
        }
    ) { padding ->
        LazyColumn(
            modifier = Modifier.fillMaxSize().contentWidth(FormMaxWidth).padding(padding),
            contentPadding = PaddingValues(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (state.isLoading) {
                item("loading") { Centered { CircularProgressIndicator(color = DeepNavy) } }
                return@LazyColumn
            }
            if (!state.canImport) {
                item("read_only") { Text(stringResource(Res.string.staff_import_read_only), color = DeepNavy.copy(alpha = 0.7f)) }
                return@LazyColumn
            }
            when (state.step) {
                StaffImportStep.SOURCE -> sourceStep(state, viewModel)
                StaffImportStep.MAPPING -> mappingStep(state, viewModel)
                StaffImportStep.PREVIEW -> previewStep(state, viewModel, onSeePlans)
                StaffImportStep.DONE -> doneStep(state, viewModel, onNavigateBack)
            }
        }
    }
}

@Composable
private fun Centered(content: @Composable () -> Unit) {
    Row(Modifier.fillMaxWidth().padding(32.dp), horizontalArrangement = Arrangement.Center) { content() }
}

@Composable
private fun SectionTitle(text: String, icon: ImageVector) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Icon(icon, contentDescription = null, tint = DeepNavy, modifier = Modifier.size(22.dp))
        Text(text, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = DeepNavy, modifier = Modifier.padding(start = 8.dp))
    }
}

@Composable
private fun Muted(text: String, modifier: Modifier = Modifier) =
    Text(text, style = MaterialTheme.typography.bodySmall, color = DeepNavy.copy(alpha = 0.7f), modifier = modifier)

// --- 1. The source -------------------------------------------------------------------------------

private fun androidx.compose.foundation.lazy.LazyListScope.sourceStep(state: StaffImportUiState, viewModel: StaffImportViewModel) {
    item("intro") { Text(stringResource(Res.string.staff_import_intro), style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.8f)) }
    item("drop") {
        SpreadsheetDropZone(
            dropTitle = stringResource(Res.string.staff_import_drop_title),
            chooseTitle = stringResource(Res.string.staff_import_choose_title),
            hint = stringResource(Res.string.staff_import_drop_hint),
            chooseLabel = stringResource(Res.string.staff_import_choose_file),
            onFile = viewModel::onFile,
            isReading = state.isReading,
            readingLabel = stringResource(Res.string.staff_import_reading)
        )
    }
    item("or") { Text(stringResource(Res.string.staff_import_or), color = DeepNavy.copy(alpha = 0.6f), modifier = Modifier.fillMaxWidth(), textAlign = androidx.compose.ui.text.style.TextAlign.Center) }
    item("paste") {
        RegularCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                SectionTitle(stringResource(Res.string.staff_import_paste_title), Icons.Default.ContentPaste)
                Muted(stringResource(Res.string.staff_import_paste_hint))
                EvoTextField(
                    value = state.pasted,
                    onValueChange = viewModel::onPastedChanged,
                    label = stringResource(Res.string.staff_import_paste_label),
                    singleLine = false,
                    minLines = 4,
                    onLight = true,
                    modifier = Modifier.fillMaxWidth().testTag(StaffImportTags.PASTE)
                )
                OutlinedButton(
                    onClick = viewModel::readPasted,
                    enabled = state.pasted.isNotBlank(),
                    modifier = Modifier.fillMaxWidth().testTag(StaffImportTags.READ_PASTED)
                ) { Text(stringResource(Res.string.staff_import_paste_read), color = DeepNavy) }
            }
        }
    }
    item("template") {
        RegularCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                SectionTitle(stringResource(Res.string.staff_import_template_title), Icons.Default.Description)
                Muted(stringResource(Res.string.staff_import_template_hint))
                TextButton(onClick = viewModel::downloadTemplate, modifier = Modifier.testTag(StaffImportTags.TEMPLATE)) {
                    Text(stringResource(Res.string.staff_import_template_download), color = DeepNavy, fontWeight = FontWeight.Bold)
                }
            }
        }
    }
    item("unsupported") {
        Row(verticalAlignment = Alignment.Top) {
            Icon(Icons.Default.Info, contentDescription = null, tint = DeepNavy.copy(alpha = 0.6f), modifier = Modifier.size(18.dp))
            Muted(stringResource(Res.string.staff_import_unsupported), Modifier.padding(start = 8.dp))
        }
    }
}

// --- 2. The mapping ------------------------------------------------------------------------------

@OptIn(ExperimentalLayoutApi::class)
private fun androidx.compose.foundation.lazy.LazyListScope.mappingStep(state: StaffImportUiState, viewModel: StaffImportViewModel) {
    item("mapping_head") {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            SectionTitle(stringResource(Res.string.staff_import_mapping_title), Icons.Default.TableChart)
            Text(
                state.fileName ?: stringResource(Res.string.staff_import_paste_label),
                fontWeight = FontWeight.Bold, color = DeepNavy
            )
            Muted(stringResource(Res.string.staff_import_mapping_hint))
            if (!state.includesPrivate) Muted(stringResource(Res.string.staff_import_private_fields_hidden))
        }
    }
    if (state.sheetNames.size > 1) {
        item("sheets") {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(stringResource(Res.string.staff_import_sheet), style = MaterialTheme.typography.labelLarge, color = DeepNavy)
                FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    state.sheetNames.forEachIndexed { index, name ->
                        FilterChip(
                            selected = index == state.sheetIndex,
                            onClick = { viewModel.selectSheet(index) },
                            label = { Text(name.ifBlank { (index + 1).toString() }) },
                            colors = FilterChipDefaults.filterChipColors(selectedContainerColor = DarkGold.copy(alpha = 0.3f))
                        )
                    }
                }
            }
        }
    }
    item("has_header") {
        Row(
            Modifier.fillMaxWidth().testTag(StaffImportTags.HAS_HEADER),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(stringResource(Res.string.staff_import_has_header), color = DeepNavy, modifier = Modifier.weight(1f))
            Switch(
                checked = state.mapping.headerRow != null,
                onCheckedChange = viewModel::setHasHeader,
                colors = SwitchDefaults.colors(checkedThumbColor = Color.White, checkedTrackColor = DeepNavy)
            )
        }
    }
    items(state.columns, key = { "column_${it.index}" }) { column ->
        ColumnCard(column, state.mapping.fields.getOrNull(column.index), state.offeredFields) { viewModel.mapColumn(column.index, it) }
    }
    item("mapping_actions") {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (!state.mapping.hasName) Text(stringResource(Res.string.staff_import_name_column_needed), color = MaterialTheme.colorScheme.error)
            GoldButton(
                text = stringResource(Res.string.staff_import_to_preview),
                onClick = viewModel::showPreview,
                enabled = state.mapping.hasName,
                modifier = Modifier.fillMaxWidth().testTag(StaffImportTags.TO_PREVIEW)
            )
            TextButton(onClick = viewModel::startOver, modifier = Modifier.align(Alignment.End)) {
                Text(stringResource(Res.string.staff_import_start_over), color = DeepNavy)
            }
        }
    }
}

@Composable
private fun ColumnCard(
    column: StaffImportColumn,
    field: StaffImportField?,
    offered: List<StaffImportField>,
    onSelect: (StaffImportField?) -> Unit
) {
    val letter = XlsxReader.columnName(column.index)
    RegularCard(Modifier.fillMaxWidth().testTag(StaffImportTags.column(column.index))) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                if (column.header.isNotEmpty()) stringResource(Res.string.staff_import_column_named, letter, column.header)
                else stringResource(Res.string.staff_import_column, letter),
                fontWeight = FontWeight.Bold, color = DeepNavy
            )
            if (column.samples.isNotEmpty()) Muted(stringResource(Res.string.staff_import_column_samples, column.samples.joinToString("; ")))
            // "Do not import" is an option like the fields (a null selection would show nothing).
            EvoDropdown(
                label = stringResource(Res.string.staff_import_column_field),
                options = listOf(FieldOption(null)) + offered.map(::FieldOption),
                selected = FieldOption(field),
                onSelected = { onSelect(it.field) },
                optionLabel = { fieldLabel(it.field) },
                onLight = true
            )
        }
    }
}

/** A choice of the "Imports as" menu: a field, or none. */
private data class FieldOption(val field: StaffImportField?)

@Composable
internal fun fieldLabel(field: StaffImportField?): String = stringResource(
    when (field) {
        null -> Res.string.staff_import_field_none
        StaffImportField.NAME -> Res.string.staff_import_field_name
        StaffImportField.FIRST_NAME -> Res.string.staff_import_field_first_name
        StaffImportField.LAST_NAME -> Res.string.staff_import_field_last_name
        StaffImportField.EMAIL -> Res.string.staff_import_field_email
        StaffImportField.PHONE -> Res.string.staff_import_field_phone
        StaffImportField.ROLE -> Res.string.staff_import_field_role
        StaffImportField.PERMISSION -> Res.string.staff_import_field_permission
        StaffImportField.CONTRACT -> Res.string.staff_import_field_contract
        StaffImportField.PAY_MODEL -> Res.string.staff_import_field_pay_model
        StaffImportField.RATE -> Res.string.staff_import_field_rate
        StaffImportField.STUDENT -> Res.string.staff_import_field_student
        StaffImportField.SHIFT_LEAD -> Res.string.staff_import_field_shift_lead
    }
)

// --- 3. The preview ------------------------------------------------------------------------------

private fun androidx.compose.foundation.lazy.LazyListScope.previewStep(
    state: StaffImportUiState,
    viewModel: StaffImportViewModel,
    onSeePlans: () -> Unit
) {
    item("summary") {
        RegularCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                SectionTitle(stringResource(Res.string.staff_import_preview_title), Icons.Default.TableChart)
                state.fileName?.let { Text(it, fontWeight = FontWeight.Bold, color = DeepNavy) }
                DataRow(stringResource(Res.string.staff_import_count_ready), state.readyRows.size.toString())
                DataRow(stringResource(Res.string.staff_import_count_fix), state.rowsToFix.toString())
                if (state.skippedRows > 0) DataRow(stringResource(Res.string.staff_import_count_skipped), state.skippedRows.toString())
                state.seatsLeft?.let { DataRow(stringResource(Res.string.staff_import_seats_left), it.toString()) }
                if (state.exceedsSeats) {
                    Text(
                        stringResource(
                            if (state.canChoosePlan) Res.string.staff_import_exceeds_seats else Res.string.staff_import_exceeds_seats_ask_owner,
                            state.seatsLeft ?: 0, state.readyRows.size
                        ),
                        // SH-220: information, not a refusal: growing past the plan never blocks.
                        color = DeepNavy
                    )
                    if (state.canChoosePlan) OutlinedButton(onClick = onSeePlans) { Text(stringResource(Res.string.staff_import_see_plans), color = DeepNavy) }
                }
                if (state.rowsToFix > 0) {
                    Text(stringResource(Res.string.staff_import_fix_first), color = MaterialTheme.colorScheme.error)
                    OutlinedButton(onClick = viewModel::skipAllWithProblems, modifier = Modifier.testTag(StaffImportTags.SKIP_ALL)) {
                        Text(stringResource(Res.string.staff_import_skip_all), color = DeepNavy)
                    }
                }
            }
        }
    }
    items(state.rows, key = { "row_${it.number}" }) { row ->
        RowCard(row, state.currency, enabled = !state.isImporting, onFix = { viewModel.startEdit(row.number) }, onSkip = { viewModel.toggleSkip(row.number) })
    }
    item("apply") {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (state.isImporting) {
                Text(stringResource(Res.string.staff_import_progress, state.importedSoFar, state.importTotal), color = DeepNavy)
            }
            val count = state.readyRows.size
            GoldButton(
                text = pluralStringResource(Res.plurals.staff_import_apply, count, count),
                onClick = viewModel::import,
                enabled = count > 0 && state.rowsToFix == 0,
                isBusy = state.isImporting,
                modifier = Modifier.fillMaxWidth().testTag(StaffImportTags.APPLY)
            )
            TextButton(onClick = viewModel::backToMapping, enabled = !state.isImporting, modifier = Modifier.align(Alignment.End)) {
                Text(stringResource(Res.string.staff_import_back_to_mapping), color = DeepNavy)
            }
        }
    }
}

@Composable
private fun RowCard(row: StaffImportRowState, currency: String, enabled: Boolean, onFix: () -> Unit, onSkip: () -> Unit) {
    val candidate = row.check.candidate
    val error = MaterialTheme.colorScheme.error
    RegularCard(Modifier.fillMaxWidth().testTag(StaffImportTags.row(row.number))) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                val icon = when {
                    row.saved -> Icons.Default.CheckCircle
                    row.needsFix || row.failure != null -> Icons.Default.ErrorOutline
                    else -> null
                }
                icon?.let { Icon(it, contentDescription = null, tint = if (row.saved) DarkGold else error, modifier = Modifier.size(20.dp).padding(end = 4.dp)) }
                Text(
                    candidate.displayName.ifEmpty { stringResource(Res.string.staff_import_row_no_name) },
                    fontWeight = FontWeight.Bold, color = DeepNavy.copy(alpha = if (row.skipped) 0.5f else 1f), modifier = Modifier.weight(1f)
                )
                Muted(stringResource(Res.string.staff_import_row, row.number))
            }
            val details = listOfNotNull(
                candidate.email,
                candidate.phone,
                candidate.role.takeIf { it.isNotEmpty() },
                candidate.permission.label(),
                candidate.contract.takeIf { it != com.evoapps.evoshift.domain.model.SystemContractType.NOT_SET }?.let { contractLabel(it) },
                candidate.rate?.let {
                    stringResource(if (candidate.isHourly) Res.string.staff_import_rate_hourly else Res.string.staff_import_rate_monthly, formatMoney(it, currency))
                },
                stringResource(Res.string.staff_import_student).takeIf { candidate.student },
                stringResource(Res.string.staff_import_field_shift_lead).takeIf { candidate.shiftLead }
            )
            if (details.isNotEmpty()) Muted(details.joinToString(" · "))
            when {
                row.saved -> Text(stringResource(Res.string.staff_import_row_added), color = DarkGold, fontWeight = FontWeight.Bold)
                row.skipped -> Muted(stringResource(Res.string.staff_import_row_skipped))
                else -> {
                    row.check.problems.forEach { Text(problemText(it), color = error, style = MaterialTheme.typography.bodySmall) }
                    row.check.notes.forEach { Muted(noteText(it)) }
                    row.failure?.let { Text(stringResource(Res.string.staff_import_row_failed, it.asString()), color = error, style = MaterialTheme.typography.bodySmall) }
                }
            }
            if (!row.saved) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.align(Alignment.End)) {
                    if (!row.skipped) {
                        TextButton(onClick = onFix, enabled = enabled, modifier = Modifier.testTag(StaffImportTags.fix(row.number))) {
                            Text(stringResource(Res.string.staff_import_fix), color = DeepNavy)
                        }
                    }
                    TextButton(onClick = onSkip, enabled = enabled, modifier = Modifier.testTag(StaffImportTags.skip(row.number))) {
                        Text(stringResource(if (row.skipped) Res.string.staff_import_include else Res.string.staff_import_skip), color = DeepNavy)
                    }
                }
            }
        }
    }
}

@Composable
internal fun problemText(problem: StaffRowProblem): String = stringResource(problemResource(problem))

internal fun problemResource(problem: StaffRowProblem): StringResource = when (problem) {
    StaffRowProblem.NAME_MISSING -> Res.string.staff_import_problem_name_missing
    StaffRowProblem.EMAIL_INVALID -> Res.string.staff_import_problem_email_invalid
    StaffRowProblem.EMAIL_DUPLICATE_IN_FILE -> Res.string.staff_import_problem_email_duplicate_in_file
    StaffRowProblem.EMAIL_ALREADY_IN_TEAM -> Res.string.staff_import_problem_email_already_in_team
    StaffRowProblem.PHONE_INVALID -> Res.string.staff_import_problem_phone_invalid
    StaffRowProblem.PERMISSION_UNKNOWN -> Res.string.staff_import_problem_permission_unknown
    StaffRowProblem.PERMISSION_NOT_ALLOWED -> Res.string.staff_import_problem_permission_not_allowed
    StaffRowProblem.CONTRACT_UNKNOWN -> Res.string.staff_import_problem_contract_unknown
    StaffRowProblem.PAY_MODEL_UNKNOWN -> Res.string.staff_import_problem_pay_model_unknown
    StaffRowProblem.RATE_NOT_A_NUMBER -> Res.string.staff_import_problem_rate_not_a_number
    StaffRowProblem.RATE_NEGATIVE -> Res.string.staff_import_problem_rate_negative
    StaffRowProblem.STUDENT_UNKNOWN -> Res.string.staff_import_problem_student_unknown
    StaffRowProblem.SHIFT_LEAD_UNKNOWN -> Res.string.staff_import_problem_shift_lead_unknown
}

@Composable
private fun noteText(note: StaffRowNote): String = stringResource(
    when (note) {
        StaffRowNote.NAME_ALREADY_IN_TEAM -> Res.string.staff_import_note_name_already_in_team
        StaffRowNote.NAME_DUPLICATE_IN_FILE -> Res.string.staff_import_note_name_duplicate_in_file
        StaffRowNote.NEW_ROLE -> Res.string.staff_import_note_new_role
        StaffRowNote.SHIFT_LEAD_NOT_CONFIGURED -> Res.string.staff_import_note_shift_lead_not_configured
    }
)

/** "Fix": the row's mapped cells as text fields, checked again on "Save and check". */
@Composable
private fun EditRowDialog(
    state: StaffImportUiState,
    row: StaffImportRowState,
    onSave: (Map<StaffImportField, String>) -> Unit,
    onDismiss: () -> Unit
) {
    val fields = state.mapping.fields.filterNotNull()
    val values = remember(row.number) { mutableStateMapOf<StaffImportField, String>().apply { fields.forEach { put(it, row.check.row[it]) } } }
    EvoDialog(
        title = stringResource(Res.string.staff_import_edit_title, row.number),
        onDismiss = onDismiss,
        confirm = DialogAction(stringResource(Res.string.staff_import_edit_save), { onSave(values.toMap()) })
    ) {
        row.check.problems.forEach { Text(problemText(it), color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall) }
        fields.forEach { field ->
            EvoTextField(
                value = values[field].orEmpty(),
                onValueChange = { values[field] = it },
                label = fieldLabel(field),
                modifier = Modifier.fillMaxWidth().testTag(StaffImportTags.editField(field))
            )
        }
    }
}

// --- 4. Done -------------------------------------------------------------------------------------

private fun androidx.compose.foundation.lazy.LazyListScope.doneStep(
    state: StaffImportUiState,
    viewModel: StaffImportViewModel,
    onBack: () -> Unit
) {
    val summary = state.summary ?: return
    item("done") {
        RegularCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                SectionTitle(stringResource(Res.string.staff_import_done_title), Icons.Default.CheckCircle)
                DataRow(stringResource(Res.string.staff_import_done_added), summary.added.toString())
                DataRow(stringResource(Res.string.staff_import_done_invited), summary.invited.toString())
                if (summary.failed > 0) DataRow(stringResource(Res.string.staff_import_done_failed), summary.failed.toString())
                if (summary.inviteFailed > 0) Text(stringResource(Res.string.staff_import_done_invite_failed, summary.inviteFailed), color = MaterialTheme.colorScheme.error)
                HorizontalDivider(color = DeepNavy.copy(alpha = 0.08f))
                Muted(stringResource(Res.string.staff_import_done_hint))
                GoldButton(
                    text = stringResource(Res.string.staff_import_done_back),
                    onClick = onBack,
                    modifier = Modifier.fillMaxWidth().testTag(StaffImportTags.DONE)
                )
                if (state.rows.any { !it.saved }) {
                    TextButton(onClick = viewModel::reviewRemaining, modifier = Modifier.align(Alignment.End)) {
                        Text(stringResource(Res.string.staff_import_done_remaining), color = DeepNavy)
                    }
                }
            }
        }
    }
}
